/**
 * DNA Reputation AI - Gestão de Reputação Online
 * Fase 8 do DNA Growth Engine
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Star, MessageSquare, TrendingUp, AlertCircle, CheckCircle,
  Send, Plus, Sparkles, RefreshCw, Loader2, ThumbsUp, ThumbsDown,
  Meh, Clock, BarChart3, Settings, ExternalLink
} from 'lucide-react';

const API_URL = window.location.origin || '';

const SENTIMENTO_CONFIG = {
  positivo: { label: 'Positivo', color: 'bg-green-500', icon: ThumbsUp },
  neutro: { label: 'Neutro', color: 'bg-gray-500', icon: Meh },
  negativo: { label: 'Negativo', color: 'bg-red-500', icon: ThumbsDown }
};

export default function DnaReputationAI() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  
  // Avaliação selecionada
  const [avaliacaoSelecionada, setAvaliacaoSelecionada] = useState(null);
  const [showResposta, setShowResposta] = useState(false);
  const [resposta, setResposta] = useState('');
  const [loadingSugestao, setLoadingSugestao] = useState(false);
  
  // Nova avaliação
  const [showNovaAvaliacao, setShowNovaAvaliacao] = useState(false);
  const [novaAvaliacao, setNovaAvaliacao] = useState({
    autor: '', nota: 5, texto: '', data_avaliacao: new Date().toISOString().split('T')[0]
  });
  
  // Filtros
  const [filtroSentimento, setFiltroSentimento] = useState('all');
  const [filtroNota, setFiltroNota] = useState('all');
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };
  
  const carregarDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/growth/reputation/dashboard`, { headers: getAuthHeaders() });
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const carregarAvaliacoes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filtroSentimento !== 'all') params.append('sentimento', filtroSentimento);
      if (filtroNota !== 'all') params.append('nota_min', filtroNota);
      
      const res = await fetch(`${API_URL}/api/growth/reputation/avaliacoes?${params.toString()}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setAvaliacoes(data.avaliacoes || []);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, [filtroSentimento, filtroNota]);
  
  const carregarPendentes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/reputation/avaliacoes/pendentes`, { headers: getAuthHeaders() });
      const data = await res.json();
      setPendentes(data.pendentes || []);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, []);
  
  const carregarCategorias = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/reputation/analise-categorias`, { headers: getAuthHeaders() });
      const data = await res.json();
      setCategorias(data.categorias || []);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, []);
  
  useEffect(() => {
    carregarDashboard();
    carregarPendentes();
  }, [carregarDashboard, carregarPendentes]);
  
  useEffect(() => {
    if (activeTab === 'avaliacoes') {
      carregarAvaliacoes();
    }
    if (activeTab === 'analise') {
      carregarCategorias();
    }
  }, [activeTab, carregarAvaliacoes, carregarCategorias]);
  
  const criarAvaliacao = async () => {
    if (!novaAvaliacao.autor) {
      toast.error('Informe o autor da avaliação');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/growth/reputation/avaliacoes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...novaAvaliacao, plataforma: 'google' })
      });
      
      if (res.ok) {
        toast.success('Avaliação registrada!');
        setShowNovaAvaliacao(false);
        setNovaAvaliacao({ autor: '', nota: 5, texto: '', data_avaliacao: new Date().toISOString().split('T')[0] });
        carregarAvaliacoes();
        carregarDashboard();
      }
    } catch (error) {
      toast.error('Erro ao registrar avaliação');
    }
  };
  
  const gerarSugestaoResposta = async () => {
    if (!avaliacaoSelecionada) return;
    
    setLoadingSugestao(true);
    try {
      const res = await fetch(`${API_URL}/api/growth/reputation/avaliacoes/${avaliacaoSelecionada.id}/gerar-resposta`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setResposta(data.sugestao || '');
    } catch (error) {
      toast.error('Erro ao gerar sugestão');
    } finally {
      setLoadingSugestao(false);
    }
  };
  
  const enviarResposta = async () => {
    if (!avaliacaoSelecionada || !resposta.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/growth/reputation/avaliacoes/${avaliacaoSelecionada.id}/resposta`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ avaliacao_id: avaliacaoSelecionada.id, texto: resposta })
      });
      
      if (res.ok) {
        toast.success('Resposta registrada!');
        setShowResposta(false);
        setResposta('');
        setAvaliacaoSelecionada(null);
        carregarAvaliacoes();
        carregarPendentes();
        carregarDashboard();
      }
    } catch (error) {
      toast.error('Erro ao registrar resposta');
    }
  };
  
  const renderStars = (nota) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < nota ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };
  
  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Star className="w-8 h-8 text-yellow-500" />
            DNA Reputation AI
          </h1>
          <p className="text-gray-500 mt-1">Gestão de Reputação Online - Google Meu Negócio</p>
        </div>
        
        <Button onClick={() => setShowNovaAvaliacao(true)} className="bg-yellow-500 hover:bg-yellow-600">
          <Plus className="w-4 h-4 mr-2" />
          Registrar Avaliação
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="avaliacoes">Avaliações</TabsTrigger>
          <TabsTrigger value="analise">Análise</TabsTrigger>
        </TabsList>
        
        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-yellow-500" />
            </div>
          ) : dashboard && (
            <>
              {/* Métricas Principais */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <Card className="bg-gradient-to-br from-yellow-400 to-yellow-500 text-white">
                  <CardContent className="p-4">
                    <p className="text-yellow-100 text-sm">Média</p>
                    <div className="flex items-center gap-2">
                      <p className="text-3xl font-bold">{dashboard.resumo?.media_nota || 0}</p>
                      <Star className="w-6 h-6 fill-white" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <p className="text-gray-500 text-sm">Total Avaliações</p>
                    <p className="text-2xl font-bold text-gray-800">{dashboard.resumo?.total_avaliacoes || 0}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <p className="text-gray-500 text-sm">Este Mês</p>
                    <p className="text-2xl font-bold text-gray-800">{dashboard.resumo?.avaliacoes_mes || 0}</p>
                  </CardContent>
                </Card>
                
                <Card className={dashboard.resumo?.pendentes_resposta > 0 ? 'border-yellow-300 bg-yellow-50' : ''}>
                  <CardContent className="p-4">
                    <p className="text-gray-500 text-sm">Pendentes</p>
                    <p className="text-2xl font-bold text-gray-800">{dashboard.resumo?.pendentes_resposta || 0}</p>
                  </CardContent>
                </Card>
                
                <Card className={dashboard.resumo?.urgentes > 0 ? 'border-red-300 bg-red-50' : ''}>
                  <CardContent className="p-4">
                    <p className="text-gray-500 text-sm">Urgentes</p>
                    <p className="text-2xl font-bold text-red-600">{dashboard.resumo?.urgentes || 0}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <p className="text-gray-500 text-sm">Taxa Resposta</p>
                    <p className="text-2xl font-bold text-green-600">{dashboard.resumo?.taxa_resposta || 0}%</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Distribuição por Estrelas */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Distribuição por Estrelas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[5, 4, 3, 2, 1].map(nota => {
                        const count = dashboard.distribuicao_estrelas?.[nota.toString()] || 0;
                        const total = dashboard.resumo?.total_avaliacoes || 1;
                        const percent = Math.round(count / total * 100) || 0;
                        
                        return (
                          <div key={nota} className="flex items-center gap-3">
                            <div className="flex items-center gap-1 w-20">
                              <span className="font-medium">{nota}</span>
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            </div>
                            <div className="flex-1 bg-gray-200 rounded-full h-4">
                              <div 
                                className={`h-4 rounded-full ${
                                  nota >= 4 ? 'bg-green-500' : nota === 3 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-500 w-16 text-right">{count} ({percent}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Por Sentimento */}
                <Card>
                  <CardHeader>
                    <CardTitle>Por Sentimento</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(SENTIMENTO_CONFIG).map(([key, config]) => {
                        const Icon = config.icon;
                        const count = dashboard.por_sentimento?.[key] || 0;
                        
                        return (
                          <div key={key} className="text-center p-4 bg-gray-50 rounded-xl">
                            <div className={`w-12 h-12 mx-auto mb-2 rounded-full ${config.color} flex items-center justify-center`}>
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <p className="text-2xl font-bold text-gray-800">{count}</p>
                            <p className="text-sm text-gray-500">{config.label}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Pendentes de Resposta */}
              {pendentes.length > 0 && (
                <Card className="border-yellow-300">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-yellow-700">
                      <AlertCircle className="w-5 h-5" />
                      Avaliações Pendentes de Resposta
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {pendentes.slice(0, 5).map(av => (
                        <div 
                          key={av.id}
                          className="flex items-center justify-between p-4 bg-white border rounded-xl hover:shadow-md transition-all cursor-pointer"
                          onClick={() => { setAvaliacaoSelecionada(av); setShowResposta(true); }}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              av.urgencia === 'alta' ? 'bg-red-100' : 'bg-yellow-100'
                            }`}>
                              <span className="font-bold">{av.nota}</span>
                            </div>
                            <div>
                              <p className="font-medium">{av.autor}</p>
                              <p className="text-sm text-gray-500 line-clamp-1">{av.texto || 'Sem comentário'}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {av.urgencia === 'alta' && (
                              <Badge variant="destructive">Urgente</Badge>
                            )}
                            <Button size="sm" variant="outline">
                              Responder
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
        
        {/* AVALIAÇÕES */}
        <TabsContent value="avaliacoes" className="space-y-4">
          {/* Filtros */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <Select value={filtroSentimento} onValueChange={setFiltroSentimento}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Sentimento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="positivo">Positivo</SelectItem>
                    <SelectItem value="neutro">Neutro</SelectItem>
                    <SelectItem value="negativo">Negativo</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={filtroNota} onValueChange={setFiltroNota}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Nota mínima" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas notas</SelectItem>
                    <SelectItem value="5">5 estrelas</SelectItem>
                    <SelectItem value="4">4+ estrelas</SelectItem>
                    <SelectItem value="3">3+ estrelas</SelectItem>
                    <SelectItem value="1">1-2 estrelas</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button variant="outline" onClick={carregarAvaliacoes}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Lista */}
          {avaliacoes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Star className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nenhuma avaliação encontrada</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {avaliacoes.map(av => {
                const sentConfig = SENTIMENTO_CONFIG[av.sentimento] || SENTIMENTO_CONFIG.neutro;
                
                return (
                  <Card key={av.id} className={av.respondida ? '' : 'border-l-4 border-l-yellow-400'}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="flex">{renderStars(av.nota)}</div>
                          <Badge className={sentConfig.color}>{sentConfig.label}</Badge>
                          {av.respondida && <Badge variant="outline" className="text-green-600">Respondida</Badge>}
                        </div>
                        <span className="text-sm text-gray-500">{av.data_avaliacao}</span>
                      </div>
                      
                      <p className="font-medium text-gray-800 mb-1">{av.autor}</p>
                      {av.texto && <p className="text-gray-600">{av.texto}</p>}
                      
                      {av.resposta && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-600 font-medium mb-1">Sua resposta:</p>
                          <p className="text-sm text-blue-800">{av.resposta.texto}</p>
                        </div>
                      )}
                      
                      {!av.respondida && (
                        <div className="mt-4 flex gap-2">
                          <Button 
                            size="sm"
                            onClick={() => { setAvaliacaoSelecionada(av); setShowResposta(true); }}
                          >
                            Responder
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
        
        {/* ANÁLISE */}
        <TabsContent value="analise" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Análise por Categorias Mencionadas</CardTitle>
              <CardDescription>Categorias mais citadas nas avaliações</CardDescription>
            </CardHeader>
            <CardContent>
              {categorias.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nenhuma categoria identificada</p>
              ) : (
                <div className="space-y-4">
                  {categorias.map((cat, idx) => {
                    const sentConfig = SENTIMENTO_CONFIG[cat.sentimento] || SENTIMENTO_CONFIG.neutro;
                    
                    return (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-4">
                          <span className="font-medium text-gray-800 capitalize">{cat.categoria}</span>
                          <Badge className={sentConfig.color}>{sentConfig.label}</Badge>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex">{renderStars(Math.round(cat.media_nota))}</div>
                          <span className="text-gray-500">{cat.mencoes} menções</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Modal Resposta */}
      <Dialog open={showResposta} onOpenChange={setShowResposta}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Responder Avaliação</DialogTitle>
          </DialogHeader>
          
          {avaliacaoSelecionada && (
            <div className="space-y-4">
              {/* Avaliação original */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex">{renderStars(avaliacaoSelecionada.nota)}</div>
                  <span className="text-sm text-gray-500">{avaliacaoSelecionada.autor}</span>
                </div>
                <p className="text-gray-700">{avaliacaoSelecionada.texto || 'Sem comentário'}</p>
              </div>
              
              {/* Gerar sugestão */}
              <Button
                variant="outline"
                onClick={gerarSugestaoResposta}
                disabled={loadingSugestao}
                className="w-full"
              >
                {loadingSugestao ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Gerar Sugestão com IA
              </Button>
              
              {/* Textarea resposta */}
              <Textarea
                placeholder="Digite sua resposta..."
                value={resposta}
                onChange={(e) => setResposta(e.target.value)}
                rows={4}
              />
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowResposta(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={enviarResposta} className="flex-1 bg-yellow-500 hover:bg-yellow-600">
                  <Send className="w-4 h-4 mr-2" />
                  Salvar Resposta
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal Nova Avaliação */}
      <Dialog open={showNovaAvaliacao} onOpenChange={setShowNovaAvaliacao}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Avaliação</DialogTitle>
            <DialogDescription>Cadastre manualmente uma avaliação do Google</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder="Nome do autor"
              value={novaAvaliacao.autor}
              onChange={(e) => setNovaAvaliacao({...novaAvaliacao, autor: e.target.value})}
            />
            
            <div>
              <label className="text-sm text-gray-500 mb-2 block">Nota</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => setNovaAvaliacao({...novaAvaliacao, nota: n})}
                    className="p-2"
                  >
                    <Star className={`w-8 h-8 ${n <= novaAvaliacao.nota ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            
            <Input
              type="date"
              value={novaAvaliacao.data_avaliacao}
              onChange={(e) => setNovaAvaliacao({...novaAvaliacao, data_avaliacao: e.target.value})}
            />
            
            <Textarea
              placeholder="Texto da avaliação (opcional)"
              value={novaAvaliacao.texto}
              onChange={(e) => setNovaAvaliacao({...novaAvaliacao, texto: e.target.value})}
              rows={3}
            />
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowNovaAvaliacao(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={criarAvaliacao} className="flex-1 bg-yellow-500 hover:bg-yellow-600">
                Registrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
